import React, { useEffect, useRef } from 'react'
import { Alert, Button, Drawer, Skeleton, Tag } from 'antd'
import { differenceInCalendarDays, startOfToday } from 'date-fns'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import { IMember } from '@vacationtracker/shared/types/team-energy'
import { PTOSuggestionsContextInfo } from '@vacationtracker/shared/types/user-insight'
import { OVERDUE_PTO_DAYS } from '@vacationtracker/shared/functions/user-insights/team-energy-rules'
import { PTOSuggestionsResponse } from '../../EnergyLevelPage/types'
import EnergyBar from './EnergyBar'
import Sparkline from './Sparkline'
import StatusPill from './StatusPill'
import MemberPtoSuggestionCard from './MemberPtoSuggestionCard'
import { useMemberPtoSuggestions } from '../hooks/useMemberPtoSuggestions'
import { trackSuggestionViewed } from '../services/teamEnergyTracking'
import { formatShortDate } from '../utils/formatShortDate'

type FormatMessage = (descriptor: { id: string }, values?: Record<string, string | number>) => string

interface IMemberDetailDrawerProps {
  member: IMember | null
  departmentName: string | undefined
  onClose: () => void
}

const MemberDetailDrawer: React.FC<IMemberDetailDrawerProps> = ({ member, departmentName, onClose }) => {
  const { formatMessage, locale } = useIntl()
  const isOptedOut = Boolean(member?.isOptedOut)
  // Skip the suggestions fetch when the member opted out - we never render them.
  const { data, loading, error, refetch } = useMemberPtoSuggestions(isOptedOut ? undefined : member?.userId)
  const lastTrackedSuggestionKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!member || !data) {
      return
    }

    const key = `${member.userId}:${data.contextInfo?.type ?? 'unknown'}:${data.suggestions.length}`
    if (lastTrackedSuggestionKeyRef.current === key) {
      return
    }

    lastTrackedSuggestionKeyRef.current = key
    trackSuggestionViewed({
      userId: member.userId,
      suggestionCount: data.suggestions.length,
      contextType: data.contextInfo?.type,
    })
  }, [member, data])

  return (
    <Drawer
      title={formatMessage({ id: 'teamEnergy.memberDetail.title' })}
      placement="right"
      width={480}
      open={Boolean(member)}
      onClose={onClose}
      destroyOnClose
      className="team-energy-page te-member-drawer"
    >
      {member && (
        <div className="te-member-drawer__body">
          <div className="te-member-drawer__header">
            <UserAvatar id={member.userId} avatar={member.imageUrl ?? undefined} name={member.name ?? member.userId} shape="circle" />
            <div className="te-member-drawer__header-text">
              <Link to={`/app/users/${member.userId}`} className="te-member-drawer__name" onClick={onClose}>
                {member.name ?? member.userId}
              </Link>
              {departmentName && <div className="te-member-drawer__subtitle">{departmentName}</div>}
            </div>
          </div>

          {isOptedOut && <Alert type="info" showIcon message={formatMessage({ id: 'teamEnergy.memberDetail.optedOut' })} />}

          {!isOptedOut && member.energy && (
            <section className="te-member-drawer__section">
              <div className="te-member-drawer__section-title">{formatMessage({ id: 'teamEnergy.memberDetail.energyLevelTitle' })}</div>
              <div className="te-member-drawer__energy-row">
                <EnergyBar score={member.energy.score} mode={member.energy.mode} />
                {member.status && <StatusPill status={member.status} />}
              </div>
              {member.weeklyScores && member.weeklyScores.length >= 2 && <Sparkline scores={member.weeklyScores} trendDirection={member.trendDirection} width={420} height={56} />}
            </section>
          )}

          {!isOptedOut && (
            <section className="te-member-drawer__section">
              <div className="te-member-drawer__section-title">{formatMessage({ id: 'teamEnergy.memberDetail.timeOffTitle' })}</div>
              <div className="te-member-drawer__pto-row">
                <span className="te-member-drawer__pto-label">{formatMessage({ id: 'teamEnergy.memberDetail.lastPtoLabel' })}</span>
                <span className="te-member-drawer__pto-value">{renderLastPto(member, formatMessage, locale)}</span>
              </div>
              <div className="te-member-drawer__pto-row">
                <span className="te-member-drawer__pto-label">{formatMessage({ id: 'teamEnergy.memberDetail.nextPtoLabel' })}</span>
                <span className="te-member-drawer__pto-value">
                  {member.nextPto
                    ? formatMessage(
                        { id: 'teamEnergy.members.lastPtoRange' },
                        {
                          startDate: formatShortDate(member.nextPto.startDate, locale),
                          endDate: formatShortDate(member.nextPto.endDate, locale),
                        }
                      )
                    : formatMessage({ id: 'teamEnergy.memberDetail.nextPtoNone' })}
                </span>
              </div>
            </section>
          )}

          {!isOptedOut && (
            <section className="te-member-drawer__section">
              <div className="te-member-drawer__section-title">
                {formatMessage({ id: 'teamEnergy.memberDetail.ptoSuggestionsTitle' })}
                <Tag className="te-member-drawer__ai-tag">{formatMessage({ id: 'teamEnergy.memberDetail.aiPowered' })}</Tag>
              </div>
              {loading && (
                <div className="te-member-drawer__suggestions-loading">
                  <Skeleton active paragraph={{ rows: 2 }} />
                  <Skeleton active paragraph={{ rows: 2 }} />
                </div>
              )}
              {!loading && error && (
                <Alert
                  type="error"
                  showIcon
                  message={error}
                  action={
                    <Button size="small" onClick={refetch}>
                      {formatMessage({ id: 'teamEnergy.memberDetail.retry' })}
                    </Button>
                  }
                />
              )}
              {!loading && !error && data && data.suggestions.length === 0 && renderEmptyState(data, member.name ?? member.userId, formatMessage, locale)}
              {!loading && !error && data && data.suggestions.length > 0 && (
                <div className="te-member-drawer__suggestions-list">
                  {data.suggestions.map((s) => (
                    <MemberPtoSuggestionCard key={`${s.startDate}-${s.endDate}`} suggestion={s} isBestOption={s.isBestSuggestion} />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </Drawer>
  )
}

function renderEmptyState(data: PTOSuggestionsResponse, memberName: string, formatMessage: FormatMessage, locale: string): React.ReactNode {
  const message = getContextEmptyMessage(data, memberName, formatMessage, locale)
  return (
    <div className="te-member-drawer__suggestions-empty">
      {message.title && <div className="te-member-drawer__suggestions-empty-title">{message.title}</div>}
      <div>{message.description}</div>
    </div>
  )
}

function getContextEmptyMessage(data: PTOSuggestionsResponse, memberName: string, formatMessage: FormatMessage, locale: string): { title: string | null; description: string } {
  const contextType = data.contextInfo?.type
  const meta = data.contextInfo?.meta

  if (contextType === PTOSuggestionsContextInfo.RECENT_RETURN) {
    const leaveEndDate = meta?.leaveEndDate
    if (leaveEndDate && new Date(leaveEndDate) >= startOfToday()) {
      return {
        title: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.onVacation.title' }),
        description: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.onVacation' }, { name: memberName, endDate: formatShortDate(leaveEndDate, locale) }),
      }
    }
    const threshold = meta?.recentLeaveThresholdDays ?? 14
    const sinceReturn = meta?.daysSinceReturn ?? 0
    const daysRemaining = Math.max(1, threshold - sinceReturn)
    return {
      title: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.recentReturn.title' }),
      description: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.recentReturn' }, { name: memberName, daysRemaining }),
    }
  }

  if (contextType === PTOSuggestionsContextInfo.UPCOMING_LEAVE) {
    const daysUntilLeave = Math.max(0, meta?.daysUntilLeave ?? 0)
    return {
      title: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.upcomingLeave.title' }),
      description: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.upcomingLeave' }, { name: memberName, daysUntilLeave }),
    }
  }

  if (contextType === PTOSuggestionsContextInfo.INSUFFICIENT_BALANCE) {
    return {
      title: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.insufficientBalance.title' }),
      description: formatMessage(
        { id: 'teamEnergy.memberDetail.suggestionsEmpty.insufficientBalance' },
        {
          name: memberName,
          currentBalance: meta?.currentBalance ?? 0,
          minBalance: meta?.minBalance ?? 1,
        }
      ),
    }
  }

  if (contextType === PTOSuggestionsContextInfo.NO_COMPELLING_REASON) {
    return {
      title: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.noCompellingReason.title' }, { name: memberName }),
      description: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.noCompellingReason' }),
    }
  }

  if (contextType === PTOSuggestionsContextInfo.REQUIREMENTS_NOT_MET) {
    return {
      title: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.requirementsNotMet.title' }),
      description: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty.requirementsNotMet' }, { name: memberName }),
    }
  }

  return {
    title: null,
    description: formatMessage({ id: 'teamEnergy.memberDetail.suggestionsEmpty' }),
  }
}

function renderLastPto(member: IMember, formatMessage: FormatMessage, locale: string): React.ReactNode {
  const endDate = member.lastPto?.endDate
  if (!endDate) {
    return formatMessage({ id: 'teamEnergy.memberDetail.noLastPto' })
  }
  const rawDiff = differenceInCalendarDays(new Date(), new Date(endDate))
  if (rawDiff <= 0) {
    return formatMessage({ id: 'teamEnergy.memberDetail.onLeaveUntil' }, { date: formatShortDate(endDate, locale) })
  }
  const overdue = rawDiff >= OVERDUE_PTO_DAYS
  return <span className={overdue ? 'te-member-drawer__pto-value--overdue' : ''}>{formatMessage({ id: 'teamEnergy.members.daysAgo' }, { days: rawDiff })}</span>
}

export default MemberDetailDrawer
